import { useEffect, useState } from "react"
import { Dict, IsolatedScan, IsolatedScanIMU, IsolatedScanMotion, IsolatedScanSystemData, IsolatedScanThermal, ScanMedia, SectionScan, Workflow } from "../types/types"

import { addWorkflow } from "../service/workflowService"
import { addSectionScan } from "../service/sectionScanService"
import { addIsolatedScan } from "../service/isolatedScanService"
import { addIsolatedScanImu } from "../service/isolatedScanImuService"
import { addScanMedia } from "../service/scanMediaService"
import { showErrorToast, showLoadingToast, showSuccessToast } from "../utils/utils"
import { addIsolatedScanMotion } from "../service/isolatedScanMotionService"
import { addIsolatedScanThermal } from "../service/isolatedScanThermalService"
import { Id, toast } from "react-toastify"
import { addIsolatedScanSystemData } from "../service/isolatedScanSystemData"

type useUploadWorkflowProps = {
  onError: (error: Error) => void;
  onUploadComplete: () => void;
}
const useUploadWorkflow = (props: useUploadWorkflowProps) => {

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [loadingToastId, setLoadingToastId] = useState<Id|undefined>(undefined)
  const csvReader = new FileReader()

  const { onError, onUploadComplete } = props

  useEffect(() => {
    if (!isLoading && loadingToastId !== undefined) {
      toast.done(loadingToastId)
    }
  }, [isLoading, loadingToastId])

  /**
   * Uploads a worklow from a csv file to stcloud that are in the format specified by this
   * application.
   * 
   * @param e file change event
   */
  const uploadWorkflow = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files === null) { return }
    if (isLoading) { return }

    setIsLoading(true)
    const fileData = e.target.files[0]
    
    if (fileData) {
      csvReader.onload = (event) => {
        if (event.target === null) { return }
        const csvOutput = event.target.result as string        
        const workflowData = csvOutput.split('\n')
        try {
          const workflow: Workflow = JSON.parse(workflowData[0])
          const sectionScans: SectionScan[] = JSON.parse(workflowData[1])

          const isolatedSansJSON = JSON.parse(workflowData[2])
          const isolatedScans: IsolatedScan[] = isolatedSansJSON.map((isolatedScanJson: IsolatedScan) => {
            const isolatedScan: IsolatedScan = {
              angle: isolatedScanJson["angle"],
              calibration_end_p1: isolatedScanJson["calibration_end_p1"],
              calibration_end_p2: isolatedScanJson["calibration_end_p2"],
              calibration_start_p1: isolatedScanJson["calibration_start_p1"],
              calibration_start_p2: isolatedScanJson["calibration_start_p2"],
              comment: isolatedScanJson["comment"],
              date: isolatedScanJson["date"],
              duration: `${isolatedScanJson["duration"]}`,
              id: isolatedScanJson["id"],
              scan_no: isolatedScanJson["scan_no"],
              section_scan_id: isolatedScanJson["section_scan_id"],
              sensor_1: JSON.stringify(isolatedScanJson["sensor_1"]),
              sensor_2: JSON.stringify(isolatedScanJson["sensor_2"]),
              sensor_3: JSON.stringify(isolatedScanJson["sensor_3"]),
              sensor_4: JSON.stringify(isolatedScanJson["sensor_4"]),
              seq_nos: JSON.stringify(isolatedScanJson["seq_nos"]),
            }
            return isolatedScan
          })
          
          const isolatedScanImusJSON = JSON.parse(workflowData[3])
          const isolatedScanImus: IsolatedScanIMU[] = isolatedScanImusJSON.map((isolatedScanImuJson: Dict) => {
            const imu: IsolatedScanIMU = {
              id: isolatedScanImuJson["id"],
              section_scan_id: isolatedScanImuJson["section_scan_id"],
              scan_no: isolatedScanImuJson["scan_no"],
              seq_nos:  JSON.stringify(isolatedScanImuJson["seq_nos"]),
              accel_x:  JSON.stringify(isolatedScanImuJson["accel_x"]),
              accel_y:  JSON.stringify(isolatedScanImuJson["accel_y"]),
              accel_z:  JSON.stringify(isolatedScanImuJson["accel_z"]),
              gyro_x:  JSON.stringify(isolatedScanImuJson["gyro_x"]),
              gyro_y:  JSON.stringify(isolatedScanImuJson["gyro_y"]),
              gyro_z:  JSON.stringify(isolatedScanImuJson["gyro_z"]),
              metal:  JSON.stringify(isolatedScanImuJson["metal"]),
              roll_angles: JSON.stringify(isolatedScanImuJson["roll_angles"]),
              pitch_angles: JSON.stringify(isolatedScanImuJson["pitch_angles"]),
              inclination_angles: JSON.stringify(isolatedScanImuJson["inclination_angles"]),
              date: isolatedScanImuJson["date"]
            }
            return imu
          })
          
          const isolatedScanMotionJSON = JSON.parse(workflowData[4])
          const isolatedScanMotion: IsolatedScanMotion[] = isolatedScanMotionJSON.map((isolatedScanMotionJson: Dict) => {
            const motion: IsolatedScanMotion = {
              id: isolatedScanMotionJson["id"],
              section_scan_id: isolatedScanMotionJson["section_scan_id"],
              scan_no: JSON.stringify(isolatedScanMotionJson["scan_no"]),
              seq_nos: JSON.stringify(isolatedScanMotionJson["seq_nos"]),
              delta_x: JSON.stringify(isolatedScanMotionJson["delta_x"]),
              delta_y: JSON.stringify(isolatedScanMotionJson["delta_y"]),
              date: isolatedScanMotionJson["date"]
            }
            return motion
          })

          const isolatedScanThermalJson = JSON.parse(workflowData[5])
          const isolatedScanThermal: IsolatedScanThermal[] = isolatedScanThermalJson.map((isolatedScanThermaljson: Dict) => {
            const thermal: IsolatedScanThermal = {
              id: isolatedScanThermaljson["id"],
              section_scan_id: isolatedScanThermaljson["section_scan_id"],
              scan_no: isolatedScanThermaljson["scan_no"],
              seq_nos: JSON.stringify(isolatedScanThermaljson["seq_nos"]),
              sensor_1: JSON.stringify(isolatedScanThermaljson["sensor_1"]),
              sensor_2: JSON.stringify(isolatedScanThermaljson["sensor_2"]),
              date: isolatedScanThermaljson["date"]
            }
            return thermal
          })
          const scanMedia: ScanMedia[] = JSON.parse(workflowData[6])
          const isolatedScanSystemData: IsolatedScanSystemData[] = JSON.parse(workflowData[7])
          saveWorkflow(
            workflow,
            sectionScans,
            isolatedScans,
            isolatedScanImus,
            isolatedScanMotion,
            isolatedScanThermal,
            isolatedScanSystemData,
            scanMedia
          )
        } catch (error) {
          showErrorToast("Invalid file format. The workflow could not be imported.")
          setIsLoading(false)
        }
      }
      csvReader.readAsText(fileData)
    }
  }

  /**
   * Makes request to post workflow, sections scans and isolated scans to the stcloud.
   * 
   * @param workflow workflow to save to stcloud
   * @param sectionScans section scans to save to stcloud
   * @param isolatedScans isolated scans to save to stcloud
   */
  const saveWorkflow = async (
    workflow: Workflow,
    sectionScans: SectionScan[],
    isolatedScans: IsolatedScan[],
    isolatedScanImus: IsolatedScanIMU[],
    isolatedScanMotion: IsolatedScanMotion[],
    isolatedScanThermal: IsolatedScanThermal[],
    isolatedScanSystemData: IsolatedScanSystemData[],
    scanMedia: ScanMedia[]|null=null
  ) => {
    setLoadingToastId(showLoadingToast("Uploading workflow"))    
    const workflowResponse = await addWorkflow(workflow)
    if (workflowResponse instanceof Error) {
      onError(workflowResponse)
      setIsLoading(false)
      return
    }
    const workflowResult = workflowResponse as Workflow    
    saveSectionScans(
      workflowResult.id,
      sectionScans,
      isolatedScans,
      isolatedScanImus,
      isolatedScanMotion,
      isolatedScanThermal,
      isolatedScanSystemData,
      scanMedia
    )
  }

  /**
   * Posts sections scans and isolated scans to stcloud.
   * 
   * @param sectionScans section scans to upload
   * @param isolatedScans isolated scans to upload
   * @param workflowId id of uploaded workflow
   */
  const saveSectionScans = async (
    workflowId: number,
    sectionScans: SectionScan[],
    isolatedScans: IsolatedScan[],
    isolatedScanImus: IsolatedScanIMU[],
    isolatedScanMotion: IsolatedScanMotion[],
    isolatedScanThermal: IsolatedScanThermal[],
    isolatedScanSystemData: IsolatedScanSystemData[],
    scanMedia: ScanMedia[]|null=null
  ) => {
    for (let i=0; i<sectionScans.length; i++) {
      const sectionScanResponse = await addSectionScan(sectionScans[i], workflowId)
      if (sectionScanResponse instanceof Error) {
        onError(sectionScanResponse)
        setIsLoading(false)
        return
      }

      const sectionScanResult: SectionScan = sectionScanResponse as SectionScan
      
      saveIsolatedScans(isolatedScans, sectionScanResult.id, sectionScans[i].id)
      saveIsolatedScanThermal(isolatedScanThermal, sectionScanResult.id, sectionScans[i].id)
      saveIsolatedScanMotion(isolatedScanMotion, sectionScanResult.id, sectionScans[i].id)
      saveScanMedia(scanMedia, sectionScanResult.id, sectionScans[i].id)
      saveIsolatedScanSystemData(isolatedScanSystemData, sectionScanResult.id, sectionScans[i].id)
      saveIsolatedScanImu(isolatedScanImus, sectionScanResult.id, sectionScans[i].id, i === sectionScans.length-1)
    } 
  }

  const saveScanMedia = async (scanMedia: ScanMedia[]|null, sectionScanId: number, localSectionScanId: number) => {
    if (scanMedia == null) { return }
    const scanMediaForSection = scanMedia.filter((media) => media.section_scan_id === localSectionScanId)
    for (let i=0; i<scanMediaForSection.length; i++) {
      const scanMediaRes = await addScanMedia(scanMediaForSection[i], sectionScanId)

      if (scanMediaRes instanceof Error) {
        onError(scanMediaRes)
        setIsLoading(false)
        return
      }
    }
  }

  /**
   * Posts isolated scans to stcloud.
   * 
   * @param isolatedScans isolated scans to upload
   * @param sectionScanId id of uploaded section scan
   */
  const saveIsolatedScans = async (isolatedScans: IsolatedScan[], cloudSectionScanId: number, localSectionScanId: number) => {
    const isolatedScansForSection = isolatedScans.filter((isolatedScan: IsolatedScan) => isolatedScan.section_scan_id === localSectionScanId)
    
    for (let i=0; i<isolatedScansForSection.length; i++) {
      const isolatedScanRes = await addIsolatedScan(isolatedScansForSection[i], cloudSectionScanId)
      if (isolatedScanRes instanceof Error) {        
        onError(isolatedScanRes)
        setIsLoading(false)
        return
      }
    }
  }

  /**
   * Posts isolated scan motion data to stcloud.
   * 
   * @param isolatedScanMotions isolated scan motion to upload
   * @param sectionScanId id of uploaded section scan
   */
  const saveIsolatedScanMotion = async (isolatedScanMotions: IsolatedScanMotion[], sectionScanId: number, localSectionScanId: number) => {
    const motionsForSection = isolatedScanMotions.filter((motion) => motion.section_scan_id === localSectionScanId)
    for (let i=0; i<motionsForSection.length; i++) {
      const motionResponse = await addIsolatedScanMotion(motionsForSection[i], sectionScanId)
      if (motionResponse instanceof Error) {
        onError(motionResponse)
        setIsLoading(false)
        return
      }
    }
  }

  /**
   * Posts isolated scan thermal data to stcloud.
   * 
   * @param isolatedScanMotions isolated scan thermal to upload
   * @param sectionScanId id of uploaded section scan
   */
  const saveIsolatedScanThermal = async (isolatedScanThermals: IsolatedScanThermal[], sectionScanId: number, localSectionScanId: number) => {
    const thermalsForSection = isolatedScanThermals.filter((thermal) => thermal.section_scan_id === localSectionScanId)
    for (let i=0; i<thermalsForSection.length; i++) {
      const motionResponse = await addIsolatedScanThermal(thermalsForSection[i], sectionScanId)
      if (motionResponse instanceof Error) {
        onError(motionResponse)
        setIsLoading(false)
        return
      }
    }
  }

  const saveIsolatedScanSystemData = async (isolatedScanSystemData: IsolatedScanSystemData[], sectionScanId: number, localSectionScanId: number) => {
    const systemDataForSection = isolatedScanSystemData.filter((systemData) => systemData.section_scan_id === localSectionScanId)
    for (let i=0; i<systemDataForSection.length; i++) {
      const systemDataResponse = await addIsolatedScanSystemData(systemDataForSection[i], sectionScanId)
      if (systemDataResponse instanceof Error) {
        onError(systemDataResponse)
        setIsLoading(false)
        return
      }
    }
  }
  
  const saveIsolatedScanImu = async (isolatedScanImus: IsolatedScanIMU[], sectionScanId: number, localSectionScanId: number, finalUpload: boolean) => {
    const imuForSection = isolatedScanImus.filter((imu) => imu.section_scan_id === localSectionScanId)
    for (let i=0; i<imuForSection.length; i++) {
      const isolatedScanRes = await addIsolatedScanImu(imuForSection[i], sectionScanId)
      if (isolatedScanRes instanceof Error) {
        onError(isolatedScanRes)
        setIsLoading(false)
        return
      }
    }
    if (finalUpload) {
      setIsLoading(false)
      onUploadComplete()
      showSuccessToast("Workflow uploaded successfully.")
    }
  }

  return [{
    isLoading,
    uploadWorkflow
  }]
}

export default useUploadWorkflow